import { select, call } from 'typed-redux-saga'
import type { SagaIterator } from 'redux-saga'
import { isAxiosError } from 'axios'
import { ApiRequestError, SilkroadTypes } from '@dialogue/services'
import type {
  DateAvailabilities,
  DateAvailabilitiesWithPreferred,
  SearchAllProvidersRequest,
} from '@dialogue/silkroad'
import { AvailabilitySlot } from '@dialogue/timekeeper'

import { RootState } from 'APP/Store/CreateStore'
import {
  FetchAvailabilitiesResult,
  FetchAvailabilitiesV2Params,
  FetchTimeBasedAvailabilitiesV3Params,
  FetchProviderBasedAvailabilitiesV3Params,
} from 'APP/Store/AppointmentBooking/types'
import { AvailabilitiesFetchedError } from '../AppointmentBooking/errors'
import { logger } from 'APP/Lib/Datadog'
import { Provider } from 'APP/Store/ProviderBooking/types'
import { PARTS_OF_DAY } from 'APP/Components/AppointmentBooking/constants'
import { AvailabilitiesPerPartsOfDay } from 'APP/Components/AppointmentBooking/utils/types'
import {
  AvailabilitiesPerPartsOfDayByDate,
  groupDateAvailabilitiesByDate,
  mapSilkroadProviders,
} from 'APP/Store/AppointmentBooking/utils/availabilityMappers'
import Silkroad from 'APP/Services/Silkroad'

export const selectAccessToken = (state: RootState) => state.login.accessToken

export type FetchAvailabilitiesEnrichedResult = FetchAvailabilitiesResult & {
  availabilities: AvailabilitySlot[]
  providers: Record<number, Provider>
}

// TODO: move to App/Services/Silkroad
export function* fetchAvailabilitiesV2(
  params: FetchAvailabilitiesV2Params,
  includePractitioner: boolean = true,
  includeLicenses: boolean = true
): Generator<any, FetchAvailabilitiesEnrichedResult, any> {
  if (!params.bookingId) {
    logger.error('Availabilities search failed: Missing bookingId', {
      experience: params.experience,
      date_from: params.dateFrom,
      date_until: params.dateUntil,
    })

    throw new AvailabilitiesFetchedError('Availabilities search failed: Missing bookingId')
  }

  try {
    const accessToken: string | null = yield* select(selectAccessToken)
    const silkroad = Silkroad.create(accessToken)

    const experience = params.experience as SilkroadTypes.AppointmentBookingExperience

    const requestParams = {
      booking_id: params.bookingId,
      date_from: params.dateFrom,
      date_until: params.dateUntil,
      provider_ids: params.providerIds,
      include_practitioner: includePractitioner,
      include_licenses: includeLicenses,
    }

    const silkroadResponse = yield* call(
      [silkroad, silkroad.searchAvailabilities],
      experience,
      requestParams
    )

    if (experience === SilkroadTypes.AppointmentBookingExperience.TimeBased) {
      const grouped = silkroadResponse.data as SilkroadTypes.TimeBasedAvailabilitiesData
      return {
        availabilitiesPerPartsOfDay: grouped as unknown as AvailabilitiesPerPartsOfDay,
        availabilities: Object.values(PARTS_OF_DAY).flatMap((part) => grouped[part] ?? []),
        providers: {},
      }
    }

    const enriched = silkroadResponse.data as SilkroadTypes.EnrichedAvailabilitiesData
    const availabilities = enriched?.availabilities ?? []
    const providers = (enriched?.providers ?? {}) as unknown as Record<number, Provider>

    return { availabilities, providers }
  } catch (error: unknown) {
    const e = error as ApiRequestError
    logger.error(
      'Failed to search availabilities from Silkroad (v2)',
      {
        status: e?.code,
        url: e?.url,
        response_body: e?.additionalInfo,
        booking_id: params.bookingId,
        experience: params.experience,
        date_from: params.dateFrom,
        date_until: params.dateUntil,
        provider_ids: params.providerIds,
        include_practitioner: includePractitioner,
        include_licenses: includeLicenses,
      },
      e
    )
    throw new AvailabilitiesFetchedError((error as Error).message)
  }
}

export function* fetchTimeBasedAvailabilitiesV3(
  params: FetchTimeBasedAvailabilitiesV3Params
): SagaIterator<AvailabilitiesPerPartsOfDayByDate> {
  if (!params.bookingId) {
    logger.error('Availabilities search failed: Missing bookingId', {
      date_from: params.dateFrom,
      date_until: params.dateUntil,
    })

    throw new AvailabilitiesFetchedError('Availabilities search failed: Missing bookingId')
  }

  try {
    const accessToken = yield* select(selectAccessToken)
    const silkroad = Silkroad.create(accessToken)

    const silkroadResponse = yield* call([silkroad, silkroad.getTimeBasedAvailabilitiesV3], {
      booking_id: params.bookingId,
      date_from: params.dateFrom,
      date_until: params.dateUntil,
      timezone: params.timezone,
    })

    return groupDateAvailabilitiesByDate(silkroadResponse.data ?? [])
  } catch (error: unknown) {
    const axiosError = isAxiosError(error) ? error : undefined
    logger.error(
      'Failed to search time-based availabilities from Silkroad (v3)',
      {
        status: axiosError?.response?.status,
        url: axiosError?.config?.url,
        response_body: axiosError?.response?.data,
        booking_id: params.bookingId,
        date_from: params.dateFrom,
        date_until: params.dateUntil,
        timezone: params.timezone,
      },
      // An AxiosError carries the request headers, bearer token included: keep them out of the logs.
      axiosError ? new Error(axiosError.message) : error
    )
    throw new AvailabilitiesFetchedError((error as Error).message)
  }
}

export type ProviderBasedAvailabilitiesV3Result<Day extends DateAvailabilities> = {
  days: Day[]
  providers: Record<number, Provider>
}

const prepareV3Request = (
  params: FetchProviderBasedAvailabilitiesV3Params
): SearchAllProvidersRequest => ({
  booking_id: params.bookingId,
  date_from: params.dateFrom,
  date_until: params.dateUntil,
  timezone: params.timezone,
  include_licenses: params.includeLicenses ?? true,
})

const createError = (message: string, context: object, error: unknown) => {
  const axiosError = isAxiosError(error) ? error : undefined

  logger.error(
    message,
    {
      ...context,
      status: axiosError?.response?.status,
      url: axiosError?.config?.url,
      response_body: axiosError?.response?.data,
    },
    // An AxiosError carries the request headers, bearer token included: keep them out of the logs.
    axiosError ? new Error(axiosError.message) : error
  )

  return new AvailabilitiesFetchedError((error as Error).message)
}

/**
 * Searches all providers over the provided window.
 */
export function* searchAllProvidersAvailabilitiesV3(
  params: FetchProviderBasedAvailabilitiesV3Params
): SagaIterator<ProviderBasedAvailabilitiesV3Result<DateAvailabilitiesWithPreferred>> {
  const request = prepareV3Request(params)

  try {
    const silkroad = Silkroad.create(yield* select(selectAccessToken))
    const response = yield* call([silkroad, silkroad.getProviderBasedAvailabilitiesV3], request)

    return {
      days: response.data ?? [],
      providers: mapSilkroadProviders(response.providers ?? {}),
    }
  } catch (error: unknown) {
    throw createError(
      'Failed to search all providers availabilities from Silkroad (v3)',
      {
        booking_id: params.bookingId,
        date_from: params.dateFrom,
        date_until: params.dateUntil,
        timezone: params.timezone,
        include_licenses: params.includeLicenses,
      },
      error
    )
  }
}

/**
 * Searches one provider over the provided window.
 */
export function* searchOneProviderAvailabilitiesV3(
  providerId: number,
  params: FetchProviderBasedAvailabilitiesV3Params
): SagaIterator<ProviderBasedAvailabilitiesV3Result<DateAvailabilities>> {
  const request = prepareV3Request(params)

  try {
    const silkroad = Silkroad.create(yield* select(selectAccessToken))
    const response = yield* call(
      [silkroad, silkroad.getOneProviderAvailabilitiesV3],
      providerId,
      request
    )

    return {
      days: response.data ?? [],
      providers: mapSilkroadProviders(response.providers ?? {}),
    }
  } catch (error: unknown) {
    throw createError(
      'Failed to search one provider availabilities from Silkroad (v3)',
      {
        booking_id: params.bookingId,
        date_from: params.dateFrom,
        date_until: params.dateUntil,
        timezone: params.timezone,
        include_licenses: params.includeLicenses,
        provider_id: providerId,
      },
      error
    )
  }
}
